export type DeviceMediaInfo = {
  /** live property indicating whether the code is running on a device
   * that looks like a desktop (ie: has hover capabilities and a fine pointer). */
  isDesktop: boolean;
  /** live property indicating whether the code is running on a device
   * that has a primary touch interface (ie: no hover and has coarse pointing capabilities). */
  isTouch: boolean;
  /** contains properties using media queries that can be observed if the
   * device hover capabilities change. */
  hover: {
    /** live media query for `(hover:hover)`. */
    hasHover: MediaQueryList;
    /** live media query for `(hover:none)`. */
    noHover: MediaQueryList;
  };
  /** contains properties using media queries that can be observed if the
   * device pointer capabilities change. */
  pointer: {
    /** live media query for `(pointer:coarse)`. */
    coarse: MediaQueryList;
    /** live media query for `(pointer:fine)`. */
    fine: MediaQueryList;
  };
};

/**
 * Uses media queries to determine desktop/touch capabilities of a device.
 * @returns Returns a DeviceMediaInfo object used to instantly
 * determine device capabilities as well as provides update coordination if the capabilities change.
 */
export function getDeviceMediaInfo(): DeviceMediaInfo {
  const coarsePointer = matchMedia('(pointer:coarse)');
  const finePointer = matchMedia('(pointer:fine)');
  const hasHover = matchMedia('(hover:hover)');
  const noHover = matchMedia('(hover:none)');

  return {
    get isDesktop() {
      return finePointer.matches && hasHover.matches;
    },
    get isTouch() {
      return coarsePointer.matches && noHover.matches;
    },
    hover: {
      get hasHover() {
        return hasHover;
      },
      get noHover() {
        return noHover;
      },
    },
    pointer: {
      get coarse() {
        return coarsePointer;
      },
      get fine() {
        return finePointer;
      },
    },
  };
}
